import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTransferExchangeRate } from "@/api/cashier";
import { moneyToMajorUnits, toMinorUnits } from "@/utils/helpers";
import { useFormatAmount } from "../useFormatAmount";
import { useAccount } from "./useAccount";
import { useAmountForm } from "./useAmountForm";

export const useCurrencyConverter = () => {
  const { t } = useTranslation();
  const { updateAmountFormData } = useAmountForm();
  const { selectedAccount, selectedToAccount } = useAccount();
  const { formatAmount, getValueFromBankingAmount } = useFormatAmount();

  const [error, setError] = useState<{
    message: string;
    position: "top" | "bottom";
  } | null>({
    message: "",
    /** we need to know where to put the error, for the top or bottom amount input box */
    position: "top",
  });

  const selectedAccountCurrency = selectedAccount?.currency;
  const selectedAccountToCurrency = selectedToAccount?.currency;

  useEffect(() => {
    if (!selectedAccountCurrency || !selectedAccountToCurrency) return;

    const accountNotSelectedMessage = t("transfer.errors.account_not_selected");
    setError((prev) => {
      if (!prev) return prev;
      return prev.message === accountNotSelectedMessage ? null : prev;
    });
  }, [selectedAccountCurrency, selectedAccountToCurrency, t]);

  /**
   * Convert the amount from processing to trading currency
   * @param processingCurrency - the currency to convert from
   * @param paymentMethod - the payment method to use for the conversion
   * @param amount - the amount to convert
   * @param isReverse - if processing -> trading is a reverse exchange
   * @returns isSuccess - if the conversion was successful
   */
  const handleAmountChange = useCallback(
    async (amount: number, isReverse: boolean) => {
      if (!selectedAccountCurrency || !selectedAccountToCurrency) {
        setError({
          message: t("transfer.errors.account_not_selected"),
          position: "bottom",
        });
        return {
          isSuccess: false,
          targetAmount: 0,
        };
      }

      if (amount <= 0) {
        setError({
          message: t("transfer.errors.zero_amount"),
          position: "bottom",
        });
        return {
          isSuccess: false,
          targetAmount: 0,
        };
      }

      updateAmountFormData({
        amountIsLoading: true,
      });

      const amountCurrencyDecimals = isReverse
        ? selectedAccountCurrency.decimals
        : selectedAccountToCurrency.decimals;

      const minorAmount = toMinorUnits(amount, amountCurrencyDecimals);

      const response = await getTransferExchangeRate({
        query: {
          "from-currency-code": isReverse
            ? selectedAccountCurrency?.code
            : selectedAccountToCurrency?.code,
          "to-currency-code": isReverse
            ? selectedAccountToCurrency?.code
            : selectedAccountCurrency?.code,
          amount: String(minorAmount),
          // `isReverse` here marks which input the customer edited (true = the
          // "from"/source field). The backend's `is-reverse` marks the markup
          // direction: it must divide the markup out when the customer entered
          // the target ("to") amount, which is the inverse of `isReverse`.
          "is-reverse": !isReverse,
        },
      });

      if (!response.data) {
        setError({
          message: t("transfer.errors.exchange_rate_unavailable"),
          position: "bottom",
        });
        return {
          isSuccess: false,
          targetAmount: 0,
        };
      }

      setError(null);

      const data = response.data;
      // The backend always quotes the source -> target rate (1 source = x target),
      // regardless of which field the customer edited, so use it directly.
      const rateNumber = Number(data.rate);

      const targetAmount = moneyToMajorUnits(data.target_money) ?? 0;
      const balance = getValueFromBankingAmount(selectedAccount.margin) ?? 0;

      if (balance <= 0) {
        setError({
          message: t("transfer.errors.insufficient_funds"),
          position: "top",
        });
        return {
          isSuccess: false,
          targetAmount: 0,
        };
      }

      if (
        (!isReverse && targetAmount > balance) ||
        (isReverse && amount > balance)
      ) {
        setError({
          message: t("transfer.errors.exceeds_limit_amount", {
            maxlimit: formatAmount(balance, selectedAccountCurrency),
          }),
          position: "top",
        });
        return {
          isSuccess: false,
          targetAmount: 0,
        };
      }

      updateAmountFormData({
        tradingValue: isReverse ? targetAmount : amount,
        processingValue: isReverse ? amount : targetAmount,
        amountIsLoading: false,
        rate: rateNumber,
      });
      return {
        isSuccess: true,
        targetAmount,
      };
    },

    [
      selectedAccount,
      selectedAccountCurrency,
      selectedAccountToCurrency,
      updateAmountFormData,
      t,
    ]
  );

  return {
    handleAmountChange,
    error,
  };
};
